import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import { authApi } from "../lib/authApi";
import { showError, showSuccess } from "../lib/toast";

interface FormState {
  password: string;
  confirmPassword: string;
}

interface FieldError {
  password?: string;
  confirmPassword?: string;
  general?: string;
}

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>({
    password: "",
    confirmPassword: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState<FieldError>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Get token from URL query params (backend sends it in the email link)
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("token");

  const getStrength = (pw: string) => {
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return score;
  };

  const strength = getStrength(form.password);
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"];
  const strengthColor = [
    "",
    "bg-red-400",
    "bg-orange-400",
    "bg-amber-400",
    "bg-amber-500",
  ];
  const strengthText = [
    "",
    "text-red-500",
    "text-orange-500",
    "text-amber-500",
    "text-amber-600",
  ];

  const validate = (): boolean => {
    const e: FieldError = {};
    if (!form.password) e.password = "Password is required.";
    else if (form.password.length < 8)
      e.password = "Must be at least 8 characters.";
    else if (!/(?=.*[A-Za-z])(?=.*\d)/.test(form.password))
      e.password = "Must contain at least one letter and one number.";
    if (!form.confirmPassword)
      e.confirmPassword = "Please confirm your password.";
    else if (form.password !== form.confirmPassword)
      e.confirmPassword = "Passwords do not match.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!token) {
      setErrors({
        general: "Invalid reset link. Please request a new password reset.",
      });
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword(token, form.password, form.confirmPassword);
      setSuccess(true);
      showSuccess("Password reset successfully! You can now login.");
    } catch (err: unknown) {
      const error = err as Error;
      setErrors({
        general: error.message || "Failed to reset password. Please try again.",
      });
      showError(error.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="relative w-full max-w-sm">
          {/* Animated Background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 rounded-full bg-orange-500/20 animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 4}s`,
                }}
              />
            ))}
          </div>

          <div className="relative p-5 bg-white border border-gray-100 shadow-lg rounded-xl animate-fade-in">
            <div className="inline-flex items-center justify-center w-12 h-12 mb-3 shadow-md bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <h2 className="mb-1 text-base font-bold text-gray-900">
              Password reset!
            </h2>
            <p className="text-[11px] text-gray-500 mb-3">
              Your password has been successfully reset. You can now sign in
              with your new password.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate("/login")}
                className="w-full py-1.5 text-[11px] font-semibold text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
      <div className="relative w-full max-w-sm">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-orange-500/20 animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 4}s`,
              }}
            />
          ))}
        </div>

        {/* Main Card */}
        <div className="relative py-4 overflow-hidden bg-white border border-gray-100 shadow-lg rounded-xl">
          {/* Header - Compact */}
          <div className="px-4 py-3 text-center bg-gradient-to-r from-orange-600 to-orange-500">
            <div className="flex items-center justify-center gap-0 font-bold text-center">
              <span className="text-white text-[1.2rem]">Learn</span>
              <span className="text-white text-[1.2rem]">Hub</span>
            </div>
            <p className="mt-1 text-[10px] text-orange-100">
              Create new password
            </p>
          </div>

          {/* Content - Compact */}
          <div className="p-4">
            {/* Header Text */}
            <div className="mb-3">
              <p className="text-[11px] text-gray-500">
                Enter your new password below
              </p>
            </div>

            {/* Token Error */}
            {!token && (
              <div className="flex items-center gap-2 p-2 mb-3 border rounded-lg border-amber-200 bg-amber-50">
                <AlertCircle className="w-3 h-3 text-amber-500 shrink-0" />
                <p className="text-[10px] font-medium text-amber-700">
                  Invalid or missing reset token. Please request a new password
                  reset.
                </p>
              </div>
            )}

            {/* General Error */}
            {errors.general && (
              <div className="flex items-center gap-2 p-2 mb-3 border border-red-200 rounded-lg bg-red-50">
                <AlertCircle className="w-3 h-3 text-red-500 shrink-0" />
                <p className="text-[10px] font-medium text-red-600">
                  {errors.general}
                </p>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-2">
              {/* Password Field */}
              <div>
                <label className="block mb-0.5 text-[10px] font-semibold text-gray-600">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                  <input
                    type={showPw ? "text" : "password"}
                    placeholder="Create a strong password"
                    value={form.password}
                    onChange={(e) => {
                      setForm({ ...form, password: e.target.value });
                      setErrors({});
                    }}
                    className={`w-full pl-7 pr-7 py-2.5 text-[11px] rounded-lg border bg-white transition-all outline-none
                      focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400
                      ${errors.password ? "border-red-300 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute text-gray-400 transition-colors -translate-y-1/2 right-2.5 top-1/2 hover:text-gray-600"
                  >
                    {showPw ? (
                      <EyeOff className="w-3 h-3" />
                    ) : (
                      <Eye className="w-3 h-3" />
                    )}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {form.password.length > 0 && (
                  <div className="mt-1.5">
                    <div className="flex gap-1 mb-0.5">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-0.5 flex-1 rounded-full transition-all duration-300 ${
                            i <= strength
                              ? strengthColor[strength]
                              : "bg-gray-200"
                          }`}
                        />
                      ))}
                    </div>
                    <p
                      className={`text-[9px] font-semibold ${strengthText[strength] || "text-gray-400"}`}
                    >
                      {strengthLabel[strength] || "Too weak"} password
                    </p>
                  </div>
                )}
                {errors.password && (
                  <p className="mt-0.5 text-[9px] text-red-500">
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block mb-0.5 text-[10px] font-semibold text-gray-600">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                  <input
                    type={showPw ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={form.confirmPassword}
                    onChange={(e) => {
                      setForm({ ...form, confirmPassword: e.target.value });
                      setErrors({});
                    }}
                    className={`w-full pl-7 pr-2 py-2.5 text-[11px] rounded-lg border bg-white transition-all outline-none
                      focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400
                      ${errors.confirmPassword ? "border-red-300 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="mt-0.5 text-[9px] text-red-500">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={loading || !token}
              className="w-full flex items-center justify-center gap-2 py-1.5 mt-3 text-[11px] font-semibold text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all disabled:opacity-50"
            >
              {loading ? (
                <>
                  <svg
                    className="w-3 h-3 text-white animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Resetting...
                </>
              ) : (
                <>
                  Reset Password
                  <ArrowRight className="w-3 h-3" />
                </>
              )}
            </button>

            {/* Back to Login */}
            <div className="mt-3 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-[9px] text-gray-500 transition-colors hover:text-orange-500"
              >
                ← Back to login
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-3 text-center text-[9px] text-gray-400">
          Password must be at least 8 characters with letters and numbers
        </p>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ResetPasswordPage;
